import { z } from 'zod/v4'

const moneySchema = z.preprocess(
  (value) =>
    value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
      ? undefined
      : value,
  z.coerce
    .number({ error: 'Enter an amount of $0.00 or more' })
    .min(0, 'Enter an amount of $0.00 or more')
)

export const cashDrawerSettingsSchema = z.object({
  expected_version: z.coerce.number().int().positive(),
  enabled: z.boolean(),
})

export const cashDrawerWorkstationSchema = z.object({
  name: z.string().trim().min(1, 'Enter a register name').max(100),
  location_id: z.coerce.number().int().positive('Choose a location'),
  is_active: z.boolean(),
})

export const cashDrawerBalancingSearchSchema = z.object({
  workstation_id: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : value),
    z.coerce.number().int().positive().optional().catch(undefined)
  ),
  session_id: z.coerce.number().int().positive().optional().catch(undefined),
  view: z.enum(['open']).optional().catch(undefined),
})

export const startCashDrawerSchema = z.object({
  opening_counted_cash: moneySchema,
  note: z.string().max(2000).optional(),
})

export const adjustCashDrawerSchema = z.object({
  direction: z.enum(['in', 'out']),
  amount: z.coerce.number().positive('Enter an amount greater than $0.00'),
  reason: z.string().trim().min(1, 'Enter a reason').max(500),
})

export const closeCashDrawerSchema = z
  .object({
    closing_counted_cash: moneySchema,
    closing_retained_cash: moneySchema,
    closing_counted_checks: moneySchema,
    note: z.string().max(2000).optional(),
  })
  .refine((values) => values.closing_retained_cash <= values.closing_counted_cash, {
    path: ['closing_retained_cash'],
    message: 'Cash left in the drawer cannot be more than the cash counted',
  })

// Compare each tender in cents; offsetting differences still need an explanation.
export function createCloseCashDrawerSchema({
  expected_cash,
  expected_checks,
}: {
  expected_cash: number | string
  expected_checks: number | string
}) {
  return closeCashDrawerSchema.superRefine((values, context) => {
    const cashDiffers =
      Math.round(values.closing_counted_cash * 100) !== Math.round(Number(expected_cash) * 100)
    const checksDiffer =
      Math.round(values.closing_counted_checks * 100) !== Math.round(Number(expected_checks) * 100)

    if ((cashDiffers || checksDiffer) && !values.note?.trim()) {
      context.addIssue({
        code: 'custom',
        path: ['note'],
        message: 'Explain the cash or check difference before finishing the count',
      })
    }
  })
}
